import React, { useState } from 'react';
import { X, User, Phone, Mail, Edit, Activity, Heart, Users, Shield, FileText, Calendar, Navigation } from 'lucide-react';
import { formatAddress, getNavigationLinks, isValidAddress } from '../../utils/addressFormatter';

const ViewClientModal = ({ client, appointments, employees, onClose, onEdit }) => {
  if (!client) return null;

  // État pour gérer l'onglet actif
  const [activeTab, setActiveTab] = useState('profil');

  // Fonction pour formater la date
  const formatDate = (date) => {
    if (!date) return 'Non renseignée';
    return date.toDate ? date.toDate().toLocaleDateString('fr-FR') : new Date(date).toLocaleDateString('fr-FR');
  };

  // Définition des onglets
  const tabs = [
    { id: 'profil', label: 'Profil', icon: User },
    { id: 'autonomie', label: 'Autonomie', icon: Activity },
    { id: 'sante', label: 'Santé', icon: Heart },
    { id: 'soutien', label: 'Soutien', icon: Users },
    { id: 'services', label: 'Services', icon: FileText },
    { id: 'historique', label: 'Historique', icon: Calendar }
  ];

  // Composant pour afficher le contenu de l'onglet Profil
  const ProfilTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <div className="w-20 h-20 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center mx-auto mb-3">
            {client.photoURL ? (
              <img src={client.photoURL} alt={`${client.firstName} ${client.lastName}`} className="w-full h-full object-cover rounded-full" />
            ) : (
              <span className="text-blue-600 dark:text-blue-400 font-semibold text-2xl">
                {client.firstName ? client.firstName.charAt(0).toUpperCase() : 'B'}
              </span>
            )}
          </div>
          <h3 className="font-bold text-gray-900 dark:text-white text-lg">{client.firstName} {client.lastName}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">{client.typeEntite || 'Particulier'}</p>
          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mt-2 ${
            client.isActive !== false 
              ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300' 
              : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300'
          }`}>
            {client.isActive !== false ? 'Actif' : 'Inactif'}
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <div className="flex items-center text-gray-700 dark:text-gray-300">
                <Mail size={16} className="mr-2 text-blue-500" />
                <span className="text-sm">{client.email || 'Non renseigné'}</span>
              </div>
              <div className="flex items-center text-gray-700 dark:text-gray-300">
                <Phone size={16} className="mr-2 text-blue-500" />
                <span className="text-sm">{client.phoneNumber || 'Non renseigné'}</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-gray-700 dark:text-gray-300">
                <span className="text-sm font-medium">Date de naissance:</span>
                <p className="text-sm">{formatDate(client.birthDate)}</p>
              </div>
              <div className="text-gray-700 dark:text-gray-300">
                <span className="text-sm font-medium">Type d'entité:</span>
                <p className="text-sm">{client.typeEntite || 'Particulier'}</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-gray-700 dark:text-gray-300">
                <span className="text-sm font-medium">Adresse:</span>
                <div className="flex items-start gap-2 mt-1">
                  <p className="text-sm flex-1">
                    {client.address ? formatAddress(client.address) : 'Non renseignée'}
                    {client.addressNumber && ` (Apt ${client.addressNumber})`}
                  </p>
                  {isValidAddress(client.address) && (() => {
                    const { googleMapsFallback } = getNavigationLinks(formatAddress(client.address), client.addressNumber);
                    return (
                      <a
                        href={googleMapsFallback}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 p-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 text-blue-500 transition-colors"
                        title="Ouvrir dans Google Maps"
                      >
                        <Navigation size={14} />
                      </a>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
        <h5 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">Informations additionnelles</h5>
        <div className="space-y-2">
          <div>
            <span className="font-medium text-gray-700 dark:text-gray-300">Personne à contacter:</span>
            <p className="text-gray-600 dark:text-gray-400">{client.emergencyContact || 'Non renseignée'}</p>
          </div>
          <div>
            <span className="font-medium text-gray-700 dark:text-gray-300">Informations additionnelles:</span>
            <p className="text-gray-600 dark:text-gray-400">{client.additionalInfo || 'Aucune information supplémentaire'}</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Composant pour afficher le contenu de l'onglet Autonomie
  const AutonomieTab = () => {
    // Vérifier si des données d'autonomie existent (soit enrichies, soit dans le questionnaire)
    const hasAutonomyData = client.autonomyAssessment?.autonomyScore || 
                           client.autonomyAssessment?.gir || 
                           client.autonomyAssessment?.needsHelp ||
                           client.assessmentQuestionnaire?.autonomy;

    return (
      <div className="space-y-6">
        {hasAutonomyData ? (
          <>
            <div className="bg-purple-50 dark:bg-purple-900/20 p-6 rounded-lg border border-purple-200 dark:border-purple-800">
              <h5 className="font-semibold text-purple-800 dark:text-purple-300 mb-4">Indicateurs clés d'autonomie</h5>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {client.autonomyAssessment?.autonomyScore && (
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                      {client.autonomyAssessment.autonomyScore}%
                    </div>
                    <div className="text-sm text-purple-600 dark:text-purple-400">Score d'autonomie</div>
                  </div>
                )}
                {client.autonomyAssessment?.gir && (
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                      GIR {client.autonomyAssessment.gir}
                    </div>
                    <div className="text-sm text-purple-600 dark:text-purple-400">Niveau de dépendance</div>
                  </div>
                )}
                {client.serviceNeeds?.estimatedWeeklyHours && (
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                      {client.serviceNeeds.estimatedWeeklyHours}h
                    </div>
                    <div className="text-sm text-purple-600 dark:text-purple-400">Heures/semaine</div>
                  </div>
                )}
                {client.assessmentQuestionnaire?.completedAt && (
                  <div className="text-center">
                    <div className="text-lg font-bold text-purple-700 dark:text-purple-300">
                      {formatDate(client.assessmentQuestionnaire.completedAt)}
                    </div>
                    <div className="text-sm text-purple-600 dark:text-purple-400">Évalué le</div>
                  </div>
                )}
              </div>
            </div>

            {client.autonomyAssessment?.needsHelp && (
              <div className="bg-purple-50 dark:bg-purple-900/20 p-6 rounded-lg border border-purple-200 dark:border-purple-800">
                <h5 className="font-semibold text-purple-800 dark:text-purple-300 mb-4">Domaines nécessitant de l'aide</h5>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(client.autonomyAssessment.needsHelp)
                    .filter(([_, needsHelp]) => needsHelp)
                    .map(([domain]) => (
                      <span key={domain} className="bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-300 text-sm px-4 py-2 rounded-full">
                        {domain === 'hygiene' && '🧼 Hygiène personnelle'}
                        {domain === 'nutrition' && '🍽️ Alimentation'}
                        {domain === 'mobility' && '🚶 Mobilité'}
                        {domain === 'housekeeping' && '🏠 Entretien du logement'}
                        {domain === 'domestic' && '🧹 Activités domestiques'}
                      </span>
                    ))}
                </div>
              </div>
            )}

            {client.autonomyAssessment?.observations && (
              <div className="bg-purple-50 dark:bg-purple-900/20 p-6 rounded-lg border border-purple-200 dark:border-purple-800">
                <h5 className="font-semibold text-purple-800 dark:text-purple-300 mb-3">Observations sur l'autonomie</h5>
                <p className="text-purple-600 dark:text-purple-300">{client.autonomyAssessment.observations}</p>
              </div>
            )}

            {/* Indication d'auto-évaluation */}
            {client.selfAssessed && (
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-center">
                  <div className="bg-green-100 dark:bg-green-800 p-2 rounded-full mr-3">
                    <User size={16} className="text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-green-800 dark:text-green-300">Auto-évalué par le bénéficiaire</h5>
                    <p className="text-green-600 dark:text-green-400 text-sm">
                      Ces données ont été renseignées directement par {client.firstName} via son auto-évaluation
                      {client.lastQuestionnaireUpdate && (
                        <span> le {formatDate(client.lastQuestionnaireUpdate)}</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Afficher un message si le questionnaire est complété mais pas enrichi */}
            {client.assessmentQuestionnaire?.status === 'complété' && !client.autonomyAssessment && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-6 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <h5 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-3">Questionnaire complété - Enrichissement nécessaire</h5>
                <p className="text-yellow-600 dark:text-yellow-300">
                  Le questionnaire d'évaluation a été complété mais les données d'autonomie n'ont pas encore été enrichies dans le profil. 
                  Utilisez le bouton "Enrichir le profil" pour synchroniser les données.
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <Activity size={48} className="mx-auto text-gray-400 mb-4" />
            <h5 className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-2">Aucune évaluation d'autonomie</h5>
            <p className="text-gray-500 dark:text-gray-500">
              {client.assessmentQuestionnaire?.status === 'complété' 
                ? 'Le questionnaire est complété mais les données d\'autonomie n\'ont pas été enrichies dans le profil.'
                : 'Le questionnaire d\'évaluation n\'a pas encore été complété ou enrichi.'
              }
            </p>
          </div>
        )}
      </div>
    );
  };

  // Composant pour afficher le contenu de l'onglet Santé
  const SanteTab = () => {
    // Vérifier si des données de santé enrichies existent
    const hasHealthData = client.healthSummary?.generalHealth ||
                         client.healthSummary?.mainPathologies ||
                         client.healthSummary?.medicationCount ||
                         client.healthSummary?.fallRisk;

    return (
      <div className="space-y-6">
        {hasHealthData ? (
          <>
            <div className="bg-pink-50 dark:bg-pink-900/20 p-6 rounded-lg border border-pink-200 dark:border-pink-800">
              <h5 className="font-semibold text-pink-800 dark:text-pink-300 mb-4">Informations médicales</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  {client.healthSummary?.generalHealth && (
                    <div>
                      <span className="font-medium text-pink-700 dark:text-pink-400">État général:</span>
                      <p className="text-pink-600 dark:text-pink-300">{client.healthSummary.generalHealth}</p>
                    </div>
                  )}
                  {client.healthSummary?.mainPathologies && (
                    <div>
                      <span className="font-medium text-pink-700 dark:text-pink-400">Pathologies principales:</span>
                      <p className="text-pink-600 dark:text-pink-300">{client.healthSummary.mainPathologies}</p>
                    </div>
                  )}
                </div>
                <div className="space-y-4">
                  {client.healthSummary?.medicationCount && (
                    <div>
                      <span className="font-medium text-pink-700 dark:text-pink-400">Nombre de médicaments:</span>
                      <p className="text-pink-600 dark:text-pink-300">{client.healthSummary.medicationCount} régulier(s)</p>
                    </div>
                  )}
                  {client.healthSummary?.fallRisk && (
                    <div>
                      <span className="font-medium text-pink-700 dark:text-pink-400">Risque de chute:</span>
                      <p className="text-pink-600 dark:text-pink-300">{client.healthSummary.fallRisk}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {client.healthSummary?.otherHealthIssues && (
              <div className="bg-pink-50 dark:bg-pink-900/20 p-6 rounded-lg border border-pink-200 dark:border-pink-800">
                <h5 className="font-semibold text-pink-800 dark:text-pink-300 mb-3">Autres problèmes de santé</h5>
                <p className="text-pink-600 dark:text-pink-300">{client.healthSummary.otherHealthIssues}</p>
              </div>
            )}

            {/* Indication d'auto-évaluation */}
            {client.selfAssessed && (
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-center">
                  <div className="bg-green-100 dark:bg-green-800 p-2 rounded-full mr-3">
                    <User size={16} className="text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-green-800 dark:text-green-300">Auto-évalué par le bénéficiaire</h5>
                    <p className="text-green-600 dark:text-green-400 text-sm">
                      Ces données ont été renseignées directement par {client.firstName} via son auto-évaluation
                      {client.lastQuestionnaireUpdate && (
                        <span> le {formatDate(client.lastQuestionnaireUpdate)}</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Afficher un message si le questionnaire est complété mais pas enrichi */}
            {client.assessmentQuestionnaire?.status === 'complété' && !client.healthSummary && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-6 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <h5 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-3">Questionnaire complété - Enrichissement nécessaire</h5>
                <p className="text-yellow-600 dark:text-yellow-300">
                  Le questionnaire d'évaluation a été complété mais les données de santé n'ont pas encore été enrichies dans le profil. 
                  Utilisez le bouton "Enrichir le profil" pour synchroniser les données.
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <Heart size={48} className="mx-auto text-blue-500 dark:text-blue-400 mb-4" />
            <h5 className="text-lg font-semibold text-blue-800 dark:text-blue-300 mb-2">Questionnaire d'évaluation requis</h5>
            <p className="text-blue-600 dark:text-blue-400 mb-4">
              Les informations médicales (allergies, traitements, niveau d'autonomie) ne sont disponibles 
              que via le questionnaire d'évaluation des bénéficiaires.
            </p>
            <div className="bg-blue-100 dark:bg-blue-800/30 p-4 rounded-lg max-w-md mx-auto">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>Pour consulter les informations médicales :</strong><br />
                1. Complétez le questionnaire d'évaluation<br />
                2. Utilisez la fonction "Enrichir le profil"<br />
                3. Les données seront automatiquement synchronisées
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Composant pour afficher le contenu de l'onglet Soutien
  const SoutienTab = () => (
    <div className="space-y-6">
      {client.emergencyContacts && client.emergencyContacts.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-lg border border-red-200 dark:border-red-800">
          <h5 className="flex items-center font-semibold text-red-800 dark:text-red-300 mb-4">
            <Shield size={16} className="mr-2" />
            Contacts d'urgence
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {client.emergencyContacts.map((contact, index) => (
              <div key={index} className="bg-red-100 dark:bg-red-800/30 p-4 rounded-lg">
                <h6 className="font-semibold text-red-800 dark:text-red-300">{contact.name}</h6>
                <p className="text-sm text-red-700 dark:text-red-400 mb-2">{contact.relationship}</p>
                <div className="space-y-1">
                  <p className="text-sm text-red-600 dark:text-red-500 flex items-center">
                    <Phone size={14} className="mr-2" />
                    {contact.phone1}
                  </p>
                  {contact.phone2 && (
                    <p className="text-sm text-red-600 dark:text-red-500 flex items-center">
                      <Phone size={14} className="mr-2" />
                      {contact.phone2}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {client.familyCaregiver && (
        <div className="bg-orange-50 dark:bg-orange-900/20 p-6 rounded-lg border border-orange-200 dark:border-orange-800">
          <h5 className="font-semibold text-orange-800 dark:text-orange-300 mb-4">Aidant familial</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <span className="font-medium text-orange-700 dark:text-orange-400">Nom:</span>
                <p className="text-orange-600 dark:text-orange-300">{client.familyCaregiver.name}</p>
              </div>
              <div>
                <span className="font-medium text-orange-700 dark:text-orange-400">Lien familial:</span>
                <p className="text-orange-600 dark:text-orange-300">{client.familyCaregiver.relationship}</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <span className="font-medium text-orange-700 dark:text-orange-400">Fréquence d'aide:</span>
                <p className="text-orange-600 dark:text-orange-300">{client.familyCaregiver.frequency}</p>
              </div>
              {client.familyCaregiver.burnoutLevel && (
                <div>
                  <span className="font-medium text-orange-700 dark:text-orange-400">Niveau de charge:</span>
                  <p className="text-orange-600 dark:text-orange-300">{client.familyCaregiver.burnoutLevel}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Composant pour afficher le contenu de l'onglet Services
  const ServicesTab = () => (
    <div className="space-y-6">
      {client.serviceNeeds ? (
        <>
          <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-lg border border-green-200 dark:border-green-800">
            <h5 className="font-semibold text-green-800 dark:text-green-300 mb-4">Besoins en services</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                {client.serviceNeeds.estimatedWeeklyHours && (
                  <div>
                    <span className="font-medium text-green-700 dark:text-green-400">Heures estimées par semaine:</span>
                    <p className="text-green-600 dark:text-green-300">{client.serviceNeeds.estimatedWeeklyHours} heures</p>
                  </div>
                )}
                {client.serviceNeeds.frequency && (
                  <div>
                    <span className="font-medium text-green-700 dark:text-green-400">Fréquence des interventions:</span>
                    <p className="text-green-600 dark:text-green-300">{client.serviceNeeds.frequency}</p>
                  </div>
                )}
              </div>
              <div className="space-y-4">
                {client.serviceNeeds.requiredServices && (
                  <div>
                    <span className="font-medium text-green-700 dark:text-green-400">Services requis:</span>
                    <p className="text-green-600 dark:text-green-300">{client.serviceNeeds.requiredServices}</p>
                  </div>
                )}
                {client.serviceNeeds.observations && (
                  <div>
                    <span className="font-medium text-green-700 dark:text-green-400">Observations:</span>
                    <p className="text-green-600 dark:text-green-300">{client.serviceNeeds.observations}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-8 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <FileText size={48} className="mx-auto text-gray-400 mb-4" />
          <h5 className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-2">Aucun service défini</h5>
          <p className="text-gray-500 dark:text-gray-500">Les besoins en services n'ont pas encore été évalués</p>
        </div>
      )}
    </div>
  );

  // Composant pour afficher le contenu de l'onglet Historique
  const HistoriqueTab = () => (
    <div className="space-y-6">
      {client.assessmentHistory && client.assessmentHistory.length > 0 ? (
        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-6 rounded-lg border border-indigo-200 dark:border-indigo-800">
          <h5 className="font-semibold text-indigo-800 dark:text-indigo-300 mb-4">Historique des évaluations</h5>
          <div className="space-y-4">
            {client.assessmentHistory.map((assessment, index) => (
              <div key={index} className="bg-indigo-100 dark:bg-indigo-800/30 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <h6 className="font-semibold text-indigo-800 dark:text-indigo-300">
                    Évaluation du {formatDate(assessment.date)}
                  </h6>
                  {assessment.autonomyScore && (
                    <span className="bg-indigo-200 dark:bg-indigo-700 text-indigo-800 dark:text-indigo-300 text-sm px-3 py-1 rounded-full">
                      {assessment.autonomyScore}% d'autonomie
                    </span>
                  )}
                </div>
                {assessment.evaluator && (
                  <p className="text-sm text-indigo-700 dark:text-indigo-400 mb-2">
                    Évalué par: {assessment.evaluator}
                  </p>
                )}
                {assessment.observations && (
                  <p className="text-sm text-indigo-600 dark:text-indigo-300">
                    {assessment.observations}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
          <h5 className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-2">Aucun historique d'évaluation</h5>
          <p className="text-gray-500 dark:text-gray-500">Aucune évaluation précédente n'a été enregistrée</p>
        </div>
      )}
    </div>
  );

  // Fonction pour afficher le contenu de l'onglet actif
  const renderActiveTabContent = () => {
    switch (activeTab) {
      case 'profil':
        return <ProfilTab />;
      case 'autonomie':
        return <AutonomieTab />;
      case 'sante':
        return <SanteTab />;
      case 'soutien':
        return <SoutienTab />;
      case 'services':
        return <ServicesTab />;
      case 'historique':
        return <HistoriqueTab />;
      default:
        return <ProfilTab />;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 dark:bg-opacity-70">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        {/* En-tête du modal */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Détails du bénéficiaire</h3>
            <p className="text-gray-600 dark:text-gray-400 mt-1">{client.firstName} {client.lastName}</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <X size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Navigation par onglets horizontaux */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-6 py-4 border-b-2 text-sm font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <IconComponent size={16} className="mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Contenu de l'onglet actif */}
        <div className="p-6">
          {renderActiveTabContent()}
        </div>

        {/* Actions */}
        <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              Fermer
            </button>
            <button
              onClick={onEdit}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center"
            >
              <Edit size={16} className="mr-2" />
              Modifier
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewClientModal;
